// ✅ 모바일 전용 ChecklistEditor - 웹사이트 코드에 영향 없음
import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import MobileReferenceModal from "../../approval/MobileReferenceModal";

const DOC_TYPE_OPTIONS = [{ value: "check", label: "점검 예정" }];

export default function MobileChecklistEditor({
  initial = {},
  onChange,
  readOnly = false,
  projectId,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const editorRef = useRef(null);

  const STATUS_BY_TYPE = {
    check: [
      { value: "planned", label: "점검 예정" },
      { value: "in_progress", label: "점검 중" },
      { value: "cancelled", label: "취소" },
    ],
  };
  const [docType, setDocType] = useState(
    (initial.doc_type || initial.docType || "check").toLowerCase()
  );

  const defaultStatus = useMemo(() => {
    if (initial.status) return initial.status;
    if (initial.docType === "result") return "completed";
    return "planned";
  }, [initial]);

  const [progress, setProgress] = useState(defaultStatus || "planned");

  useEffect(() => {
    const options = STATUS_BY_TYPE[docType] || [];
    const validValues = options.map((o) => o.value);
    if (!validValues.includes(progress)) {
      setProgress(validValues[0] || "planned");
    }
  }, [docType, progress]);

  const [title, setTitle] = useState(initial.title || "");
  const [checkType, setCheckType] = useState(initial.check_type || "공종별");
  const [memo, setMemo] = useState(initial.memo || "");

  const safeInitDate =
    typeof initial.check_date === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(initial.check_date)
      ? initial.check_date
      : "";

  const [checkDate, setCheckDate] = useState(safeInitDate);

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkedDoc, setLinkedDoc] = useState(initial.linkedDoc || null);

  const [rows, setRows] = useState(
    Array.isArray(initial.items) && initial.items.length > 0
      ? initial.items.map((r) => ({ id: crypto.randomUUID(), ...r }))
      : [
          {
            id: crypto.randomUUID(),
            category: "",
            item: "",
            criteria: "",
            result: "NA",
            remark: "",
          },
        ]
  );

  // ReferenceModal 상태 및 핸들러
  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [referenceUserDetails, setReferenceUserDetails] = useState([]);
  const [approvalLines, setApprovalLines] = useState([]);
  const [viewMode, setViewMode] = useState("ALL");

  const openReferenceModal = () => setIsReferenceModalOpen(true);
  const closeReferenceModal = () => setIsReferenceModalOpen(false);

  const handleReferenceChange = (ids) => setReferenceUserIds(ids);

  const handleReferenceSave = (ids, usersForFront = []) => {
    setReferenceUserIds(ids);
    setReferenceUserDetails(
      (usersForFront || []).filter((u) => u.id !== me.id)
    );
    return true;
  };

  const progressLabel = useMemo(() => {
    const option = (STATUS_BY_TYPE[docType] || []).find(
      (opt) => opt.value === progress
    );
    return option?.label || "미정";
  }, [docType, progress]);

  // 자동 제목
  const autoTitle = useMemo(() => {
    return `[안전점검] ${checkType} - ${checkDate} (${progressLabel})`;
  }, [checkType, checkDate, progressLabel]);

  // 부모로 전달
  useEffect(() => {
    if (!onChange) return;

    const meta = {
      progress,
      doc_date: checkDate,
      check_type: checkType,
      check_date: checkDate,
      memo,
      items: rows.map(({ id, ...rest }) => rest),
      reference_users:
        viewMode === "ALL"
          ? "ALL"
          : viewMode === "ME"
          ? [me.id]
          : [...referenceUserIds, me.id],
      view_mode: viewMode,
    };

    onChange({
      dns: initial.dns || "",
      title: title?.trim() || autoTitle,
      doc_type: docType,
      status: "none",
      meta,
    });
  }, [
    docType,
    progress,
    title,
    memo,
    checkType,
    checkDate,
    rows,
    autoTitle,
    onChange,
    linkedDoc,
    viewMode,
    referenceUserIds,
    me,
    initial.dns,
  ]);

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden">
      {/* 제목 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          제목
        </label>
        <input
          type="text"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={autoTitle}
          disabled={readOnly}
        />
        <p className="text-xs text-gray-500">
          미입력 시 자동 제목: {autoTitle}
        </p>
      </div>

      {/* 상태 + 점검일 */}
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            상태
          </label>
          <select
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={progress}
            onChange={(e) => setProgress(e.target.value)}
            disabled={readOnly}
          >
            {(STATUS_BY_TYPE[docType] || []).map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            {docType === "check"
              ? "점검 예정일"
              : docType === "result"
              ? "점검 완료일"
              : "점검일"}
          </label>
          <input
            type="date"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={checkDate}
            onChange={(e) => setCheckDate(e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* 문서 연결 */}
      {docType === "result" && !readOnly && (
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-700 block">
            문서 연결
          </label>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="btn-basic text-xs px-3 py-2 w-full"
              onClick={() => setLinkModalOpen(true)}
            >
              예정 문서 연결
            </button>
            {linkedDoc && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
                연결됨: <b>{linkedDoc.title}</b>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 점검 사항 작성 및 담당자 선택 */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-gray-700 block">
          점검 사항 작성 및 담당자 선택
        </label>
        <div className="text-xs text-gray-500 mb-2">
          {progressLabel} 항목을 입력하세요. 붙여넣기로 사진 첨부 가능.
        </div>
        <div className="min-w-0 w-full">
          <TinyMCEeditor
            initialValue={memo}
            editorRef={editorRef}
            onChange={(v) => setMemo(v)}
          />
        </div>
      </div>

      {/* 조회 권한 옵션 */}
      <div className="space-y-3 border-t border-gray-200 pt-4">
        <div className="text-sm font-semibold text-gray-900">
          조회 권한 옵션
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 min-w-0">
          <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50 flex-shrink-0 whitespace-nowrap">
            <input
              type="radio"
              className="w-3.5 h-3.5 text-blue-600 flex-shrink-0"
              checked={viewMode === "ALL"}
              onChange={() => setViewMode("ALL")}
            />
            <span className="text-xs text-gray-700">
              전체 조회
            </span>
          </label>

          <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50 flex-shrink-0 whitespace-nowrap">
            <input
              type="radio"
              className="w-3.5 h-3.5 text-blue-600 flex-shrink-0"
              checked={viewMode === "REF"}
              onChange={() => setViewMode("REF")}
            />
            <span className="text-xs text-gray-700">
              일부 조회
            </span>
          </label>

          <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-gray-50 flex-shrink-0 whitespace-nowrap">
            <input
              type="radio"
              className="w-3.5 h-3.5 text-blue-600 flex-shrink-0"
              checked={viewMode === "ME"}
              onChange={() => setViewMode("ME")}
            />
            <span className="text-xs text-gray-700">나만 보기</span>
          </label>
        </div>

        {viewMode === "REF" && (
          <div className="space-y-2 mt-3">
            <button
              type="button"
              className="btn-basic text-sm px-4 py-2 w-full"
              onClick={openReferenceModal}
            >
              조회권한 설정
            </button>

            <p className="text-xs text-gray-500">
              선택된 사용자에게 알림이 발송됩니다.
            </p>

            {referenceUserDetails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {referenceUserDetails.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 border border-gray-200 text-xs"
                  >
                    {u.last_name}
                    {u.first_name}
                    <span className="text-gray-500">
                      {u.position}/{u.company}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 조회권한 모달 */}
      <MobileReferenceModal
        isOpen={isReferenceModalOpen}
        onClose={closeReferenceModal}
        approvalLines={approvalLines}
        selectedIds={referenceUserIds}
        onChange={handleReferenceChange}
        onSave={handleReferenceSave}
        name="일정 조회 가능 사용자"
      />
    </div>
  );
}

